import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    Box,
    List,
    ListItem,
    ListItemText,
    Chip,
    CircularProgress
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { STATUS_OPTIONS } from '../constants/invoiceManagementConstants';

const StatusUpdateDialog = ({
    open,
    onClose,
    onConfirm,
    selectedItems,
    invoiceData,
    t
}) => {
    const [selectedStatus, setSelectedStatus] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const { t: translate } = useTranslation();

    const getStatusText = (status) => {
        const statusMap = {
            'issued': translate('invoice.statusOptions.issued'),
            'canceled': translate('invoice.statusOptions.canceled'),
            'completed': translate('invoice.statusOptions.completed'),
            'paid': translate('invoice.statusOptions.paid'),
            'partial_paid': translate('invoice.statusOptions.partial_paid')
        };
        return statusMap[status] || status;
    };

    const getStatusColor = (status) => {
        const statusColors = {
            'issued': 'primary',
            'canceled': 'error',
            'completed': 'success',
            'paid': 'success',
            'partial_paid': 'warning'
        };
        return statusColors[status] || 'default';
    };

    const selectedInvoices = invoiceData.filter(item => selectedItems.includes(item.invoice_id));

    const handleConfirm = async () => {
        if (selectedStatus) {
            setIsLoading(true);
            try {
                await onConfirm(selectedStatus);
                setSelectedStatus('');
                onClose();
            } finally {
                setIsLoading(false);
            }
        }
    };

    const handleClose = () => {
        setSelectedStatus('');
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {t('invoice.statusUpdateTitle')}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>
                        {t('invoice.selectedInvoices', { count: selectedItems.length })}
                    </Typography>
                    <List dense>
                        {selectedInvoices.map((invoice) => (
                            <ListItem key={invoice.invoice_id} sx={{ flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                        {invoice.invoice_no}
                                    </Typography>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                            <Typography variant="body2" color="textSecondary">
                                                {invoice.customer_name} - {invoice.manager_name}
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 3, fontSize: '0.875rem' }}>
                                                <Box>
                                                    <Typography variant="body2" color="textSecondary" component="span">
                                                        AMOUNT: 
                                                    </Typography>
                                                    <Typography variant="body2" component="span" sx={{ fontWeight: 'bold', ml: 0.5 }}>
                                                        {invoice.currency} {Number(invoice.amount).toLocaleString()}
                                                    </Typography>
                                                </Box>
                                                <Box>
                                                    <Typography variant="body2" color="textSecondary" component="span">
                                                        VAT: 
                                                    </Typography>
                                                    <Typography variant="body2" component="span" sx={{ fontWeight: 'bold', ml: 0.5 }}>
                                                        {invoice.currency} {Number(invoice.vat_amount).toLocaleString()}
                                                    </Typography>
                                                </Box>
                                                <Box>
                                                    <Typography variant="body2" color="textSecondary" component="span">
                                                        TOTAL: 
                                                    </Typography>
                                                    <Typography variant="body2" component="span" sx={{ fontWeight: 'bold', ml: 0.5, color: 'primary.main' }}>
                                                        {invoice.currency} {Number(invoice.total_amount).toLocaleString()}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                        <Chip
                                            label={getStatusText(invoice.status)}
                                            color={getStatusColor(invoice.status)}
                                            variant={invoice.status === 'issued' ? 'outlined' : 'filled'}
                                            size="small"
                                            sx={{
                                                fontWeight: 'bold',
                                                minWidth: '80px',
                                                '& .MuiChip-label': {
                                                    fontSize: '0.75rem'
                                                }
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </ListItem>
                        ))}
                    </List>
                </Box>

                <FormControl fullWidth>
                    <InputLabel>{t('invoice.newStatus')}</InputLabel>
                    <Select
                        value={selectedStatus}
                        onChange={(e) => setSelectedStatus(e.target.value)}
                        label={t('invoice.newStatus')}
                    >
                        {STATUS_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {translate(option.label)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>


            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={isLoading}>
                    {t('common.cancel')}
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    disabled={!selectedStatus || isLoading}
                    startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : null}
                >
                    {isLoading ? t('common.processing') : t('common.confirm')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default StatusUpdateDialog; 